// Modal form for creating or editing a supplier — ABN checksum validation and a
// repeatable multiple-contacts editor (name/role/email/phone per row, a single
// primary radio across rows). Contacts are canonicalised client-side on submit
// to mirror the server (trim, drop substance-less rows, exactly-one-primary,
// cap at 20) and sent as `contacts`; the legacy flat contact fields are gone.

import { useState } from "react";
import { Modal, Button } from "../common";
import type {
  Supplier,
  Contact,
  CreateSupplierInput,
  SupplierStatus,
} from "../../types";

const ABN_WEIGHTS = [10, 1, 3, 5, 7, 9, 11, 13, 15, 17, 19];

/** Maximum number of contacts persisted per supplier — mirrors backend MAX_CONTACTS. */
const MAX_CONTACTS = 20;

function stripSpaces(value: string): string {
  return value.replace(/\s/g, "");
}

function validateABN(abn: string): boolean {
  const stripped = stripSpaces(abn);
  if (!/^\d{11}$/.test(stripped)) return false;
  const digits = stripped.split("").map(Number);
  digits[0] -= 1;
  const sum = digits.reduce((acc, digit, i) => acc + digit * ABN_WEIGHTS[i], 0);
  return sum % 89 === 0;
}

/** Editor row shape — every field is a controlled string so inputs never see
 * `undefined`; the optional `Contact` shape is produced only on submit. */
interface ContactRow {
  name: string;
  role: string;
  email: string;
  phone: string;
  primary: boolean;
}

const emptyContact = (): ContactRow => ({
  name: "",
  role: "",
  email: "",
  phone: "",
  primary: false,
});

/** A contact row has substance when any of name/email/phone is non-empty (role alone is not enough). */
function hasContactSubstance(contact: ContactRow): boolean {
  return Boolean(contact.name.trim() || contact.email.trim() || contact.phone.trim());
}

/**
 * Client-side canonical form for the contacts list, mirroring the server's
 * canonicaliseContacts: trim every field, drop substance-less rows (role-only
 * or all-empty), cap at MAX_CONTACTS, and coerce exactly one primary across the
 * survivors. Omits empty string fields so the wire shape stays compact.
 */
function canonicaliseContacts(contacts: ContactRow[]): Contact[] {
  const survivors = contacts.filter(hasContactSubstance).slice(0, MAX_CONTACTS);
  if (survivors.length === 0) return [];

  const markedIndex = survivors.findIndex((c) => c.primary === true);
  const winner = markedIndex === -1 ? 0 : markedIndex;

  return survivors.map((contact, index) => {
    const trimmed: Contact = { primary: index === winner };
    const name = contact.name.trim();
    const role = contact.role.trim();
    const email = contact.email.trim();
    const phone = contact.phone.trim();
    if (name) trimmed.name = name;
    if (role) trimmed.role = role;
    if (email) trimmed.email = email;
    if (phone) trimmed.phone = phone;
    return trimmed;
  });
}

/** Builds the initial editor rows for a target supplier, seeding from the
 * contacts array, then legacy flat fields, then a single empty row. */
function seedContacts(supplier: Supplier | undefined): ContactRow[] {
  if (supplier?.contacts && supplier.contacts.length > 0) {
    const rows = supplier.contacts.map((c) => ({
      name: c.name ?? "",
      role: c.role ?? "",
      email: c.email ?? "",
      phone: c.phone ?? "",
      primary: c.primary === true,
    }));
    const markedIndex = rows.findIndex((c) => c.primary);
    const winner = markedIndex === -1 ? 0 : markedIndex;
    return rows.map((c, i) => ({ ...c, primary: i === winner }));
  }

  const { contactName, contactEmail, contactPhone } = supplier ?? {};
  if (contactName || contactEmail || contactPhone) {
    return [
      {
        name: contactName ?? "",
        role: "",
        email: contactEmail ?? "",
        phone: contactPhone ?? "",
        primary: true,
      },
    ];
  }

  return [{ ...emptyContact(), primary: true }];
}

interface SupplierFormModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSubmit: (
    data: CreateSupplierInput & { status?: SupplierStatus },
  ) => Promise<void>;
  supplier?: Supplier;
  isLoading?: boolean;
}

export default function SupplierFormModal({
  isOpen,
  onClose,
  onSubmit,
  supplier,
  isLoading = false,
}: SupplierFormModalProps) {
  const [name, setName] = useState("");
  const [abn, setAbn] = useState("");
  const [acn, setAcn] = useState("");
  const [contacts, setContacts] = useState<ContactRow[]>(() =>
    seedContacts(undefined),
  );
  const [address, setAddress] = useState("");
  const [notes, setNotes] = useState("");
  const [status, setStatus] = useState<SupplierStatus>("active");
  const [abnError, setAbnError] = useState("");

  // Pattern 0: re-seed form fields during render whenever the modal opens
  // for a different target (new vs edit, or a different supplier record).
  const seedKey = isOpen ? `${supplier?.id ?? "new"}` : null;
  const [seededFor, setSeededFor] = useState<string | null>(null);
  if (seedKey !== null && seedKey !== seededFor) {
    setSeededFor(seedKey);
    setContacts(seedContacts(supplier));
    if (supplier) {
      setName(supplier.name);
      setAbn(supplier.abn);
      setAcn(supplier.acn || "");
      setAddress(supplier.address || "");
      setNotes(supplier.notes || "");
      setStatus(supplier.status);
    } else {
      setName("");
      setAbn("");
      setAcn("");
      setAddress("");
      setNotes("");
      setStatus("active");
    }
    setAbnError("");
  }

  const updateContact = (index: number, patch: Partial<ContactRow>) => {
    setContacts((prev) =>
      prev.map((c, i) => (i === index ? { ...c, ...patch } : c)),
    );
  };

  const setPrimary = (index: number) => {
    setContacts((prev) => prev.map((c, i) => ({ ...c, primary: i === index })));
  };

  const addContact = () => {
    setContacts((prev) => [...prev, emptyContact()]);
  };

  const removeContact = (index: number) => {
    setContacts((prev) => {
      const wasPrimary = prev[index]?.primary === true;
      const next = prev.filter((_, i) => i !== index);
      if (next.length === 0) return next;
      // Removing the primary row reassigns primary to the first survivor;
      // removing a non-primary row leaves the existing primary intact.
      if (wasPrimary || !next.some((c) => c.primary)) {
        return next.map((c, i) => ({ ...c, primary: i === 0 }));
      }
      return next;
    });
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setAbnError("");

    if (abn && !validateABN(abn)) {
      setAbnError("Invalid ABN checksum");
      return;
    }

    const canonicalContacts = canonicaliseContacts(contacts);

    const data: CreateSupplierInput & { status?: SupplierStatus } = {
      name,
      abn: stripSpaces(abn),
      ...(canonicalContacts.length > 0 && { contacts: canonicalContacts }),
      ...(acn && { acn }),
      ...(address && { address }),
      ...(notes && { notes }),
      ...(supplier && { status }),
    };

    await onSubmit(data);
  };

  const isEditing = !!supplier;

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title={isEditing ? "Edit Supplier" : "New Supplier"}
      maxWidth="sm:max-w-xl"
    >
      <form onSubmit={handleSubmit} className="space-y-4">
        <div>
          <label htmlFor="supplier-name" className="label">
            Name
          </label>
          <input
            id="supplier-name"
            type="text"
            required
            value={name}
            onChange={(e) => setName(e.target.value)}
            className="input"
            maxLength={200}
          />
        </div>

        <div className="grid grid-cols-2 gap-4">
          <div>
            <label htmlFor="supplier-abn" className="label">
              ABN
            </label>
            <input
              id="supplier-abn"
              type="text"
              value={abn}
              onChange={(e) => {
                setAbn(e.target.value);
                setAbnError("");
              }}
              className="input"
              maxLength={14}
            />
            {abnError && (
              <p className="mt-1 text-caption text-red-600 dark:text-red-400">
                {abnError}
              </p>
            )}
          </div>
          <div>
            <label htmlFor="supplier-acn" className="label">
              ACN
            </label>
            <input
              id="supplier-acn"
              type="text"
              value={acn}
              onChange={(e) => setAcn(e.target.value)}
              className="input"
            />
          </div>
        </div>

        <fieldset className="space-y-3">
          <legend className="text-h4 text-slate-900 dark:text-slate-100">
            Contacts
          </legend>

          {contacts.map((contact, index) => (
            <fieldset
              key={index}
              aria-label={`Contact ${index + 1}`}
              className="rounded-md border border-slate-200 p-3 dark:border-slate-700"
            >
              <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                <div>
                  <label
                    htmlFor={`supplier-contact-${index}-name`}
                    className="label"
                  >
                    Name
                  </label>
                  <input
                    id={`supplier-contact-${index}-name`}
                    type="text"
                    value={contact.name}
                    onChange={(e) =>
                      updateContact(index, { name: e.target.value })
                    }
                    className="input"
                  />
                </div>
                <div>
                  <label
                    htmlFor={`supplier-contact-${index}-role`}
                    className="label"
                  >
                    Role / title
                  </label>
                  <input
                    id={`supplier-contact-${index}-role`}
                    type="text"
                    value={contact.role}
                    onChange={(e) =>
                      updateContact(index, { role: e.target.value })
                    }
                    className="input"
                  />
                </div>
                <div>
                  <label
                    htmlFor={`supplier-contact-${index}-email`}
                    className="label"
                  >
                    Email
                  </label>
                  <input
                    id={`supplier-contact-${index}-email`}
                    type="email"
                    value={contact.email}
                    onChange={(e) =>
                      updateContact(index, { email: e.target.value })
                    }
                    className="input"
                  />
                </div>
                <div>
                  <label
                    htmlFor={`supplier-contact-${index}-phone`}
                    className="label"
                  >
                    Phone
                  </label>
                  <input
                    id={`supplier-contact-${index}-phone`}
                    type="text"
                    value={contact.phone}
                    onChange={(e) =>
                      updateContact(index, { phone: e.target.value })
                    }
                    className="input"
                  />
                </div>
              </div>

              <div className="mt-3 flex items-center justify-between">
                <label className="flex items-center gap-2 text-body-sm text-slate-700 dark:text-slate-300">
                  <input
                    type="radio"
                    name="supplier-primary-contact"
                    aria-label={`Primary contact (Contact ${index + 1})`}
                    checked={contact.primary === true}
                    onChange={() => setPrimary(index)}
                    className="h-4 w-4 border-slate-300 text-brand-600 focus:ring-brand-500 dark:border-slate-600"
                  />
                  Primary
                </label>
                <button
                  type="button"
                  onClick={() => removeContact(index)}
                  aria-label={`Remove Contact ${index + 1}`}
                  className="text-body-sm font-medium text-slate-500 hover:text-red-600 dark:text-slate-400 dark:hover:text-red-400"
                >
                  Remove
                </button>
              </div>
            </fieldset>
          ))}

          <Button type="button" variant="secondary" size="sm" onClick={addContact}>
            + Add contact
          </Button>
        </fieldset>

        <div>
          <label htmlFor="supplier-address" className="label">
            Address
          </label>
          <input
            id="supplier-address"
            type="text"
            value={address}
            onChange={(e) => setAddress(e.target.value)}
            className="input"
          />
        </div>

        <div>
          <label htmlFor="supplier-notes" className="label">
            Notes
          </label>
          <textarea
            id="supplier-notes"
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            className="input"
            rows={3}
          />
        </div>

        {isEditing && (
          <div>
            <label htmlFor="supplier-status" className="label">
              Status
            </label>
            <select
              id="supplier-status"
              value={status}
              onChange={(e) => setStatus(e.target.value as SupplierStatus)}
              className="input"
            >
              <option value="active">Active</option>
              <option value="inactive">Inactive</option>
            </select>
          </div>
        )}

        <div className="flex justify-end gap-2 pt-2">
          <Button type="button" variant="secondary" onClick={onClose}>
            Cancel
          </Button>
          <Button type="submit" loading={isLoading}>
            {isEditing ? "Save" : "Create"}
          </Button>
        </div>
      </form>
    </Modal>
  );
}
